import { withManualCache, generateCacheKey, DEFAULT_CACHE_TTL } from "./cache-service";

const ORG_KEY = process.env.NEXT_PUBLIC_ORGANIZATION_KEY;
const API_URL = process.env.NEXT_PUBLIC_API_URL;

/**
 * Fetches SEO data for a given slug with caching.
 */
export async function fetchSeoData(slug = "home-page") {
  const url = `${API_URL}/api/seo-data`;
  const payload = {
    organization_key: ORG_KEY,
    slug: slug,
  };
  const cacheKey = generateCacheKey(url, 'POST', payload);
  return withManualCache(cacheKey, async () => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        next: { revalidate: DEFAULT_CACHE_TTL },
      });
      const data = await response.json();
      return data?.data?.data || null;
    } catch (error) {
      return null;
    }
  });
}

/**
 * Fetches header/setting data with caching.
 */
export async function fetchSettingData() {
  const url = `${API_URL}/api/setting-data`;
  const payload = { organization_key: ORG_KEY };
  const cacheKey = generateCacheKey(url, 'POST', payload);
  return withManualCache(cacheKey, async () => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        next: { revalidate: DEFAULT_CACHE_TTL },
      });
      const data = await response.json();
      return data?.data || null;
    } catch (error) {
      return null;
    }
  });
}

/**
 * Fetches navigation/common data with caching.
 */
export async function fetchNavigationData() {
  const url = `${API_URL}/api/organization-data`;
  const payload = { organization_key: ORG_KEY };
  const cacheKey = generateCacheKey(url, 'POST', payload);
  return withManualCache(cacheKey, async () => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        next: { revalidate: DEFAULT_CACHE_TTL }, // Enable Next.js Data Cache
      });
      const data = await response.json();
      return data?.data || null;
    } catch (error) {
      return null;
    }
  });
}

/**
 * Fetches organization/features data with caching.
 */
export async function fetchFeatures() {
  return await fetchNavigationData();
}

/**
 * Fetches page data for a given slug with caching.
 */
export async function fetchPageData(slug) {
  const url = `${API_URL}/api/page-data?organization_key=${ORG_KEY}&slug=${slug}`;
  const cacheKey = generateCacheKey(url, 'GET');
  return withManualCache(cacheKey, async () => {
    try {
      const response = await fetch(url, {
        method: "GET",
        next: { revalidate: DEFAULT_CACHE_TTL },
      });
      const data = await response.json();
      return data?.data || null;
    } catch (error) {
      console.error(`Error fetching page data for ${slug}:`, error);
      return null;
    }
  });
}

/**
 * Fetches product details with caching.
 */
export async function fetchProductData(slug) {
  const url = `${API_URL}/api/product-data`;
  const payload = { organization_key: ORG_KEY, slug: slug };
  const cacheKey = generateCacheKey(url, 'POST', payload);
  return withManualCache(cacheKey, async () => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        next: { revalidate: DEFAULT_CACHE_TTL },
      });
      const data = await response.json();
      return data?.data || null;
    } catch (error) {
      console.error(`Error fetching product data for ${slug}:`, error);
      return null;
    }
  });
}

/**
 * Fetches blog details with caching.
 */
export async function fetchBlogData(slug) {
  const url = `${API_URL}/api/blog-data`;
  const payload = { organization_key: ORG_KEY, slug: slug };
  const cacheKey = generateCacheKey(url, 'POST', payload);
  return withManualCache(cacheKey, async () => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        next: { revalidate: DEFAULT_CACHE_TTL },
      });
      const data = await response.json();
      return data?.data || null;
    } catch (error) {
      console.error(`Error fetching blog data for ${slug}:`, error);
      return null;
    }
  });
}

/**
 * Fetches category details with caching.
 */
export async function fetchCategoryData(slug) {
  const url = `${API_URL}/api/category-data`;
  const payload = { organization_key: ORG_KEY, slug: slug };
  const cacheKey = generateCacheKey(url, 'POST', payload);
  return withManualCache(cacheKey, async () => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        next: { revalidate: DEFAULT_CACHE_TTL },
      });
      const data = await response.json();
      return data?.data || null;
    } catch (error) {
      console.error(`Error fetching category data for ${slug}:`, error);
      return null;
    }
  });
}



/**
 * Fetches Google Tag data (organization data) with caching.
 */
export async function fetchGoogleTagData() {
  const url = `${API_URL}/api/organization-data`;
  const payload = { organization_key: ORG_KEY };
  const cacheKey = generateCacheKey(url, 'POST', payload);
  return withManualCache(cacheKey, async () => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        next: { revalidate: DEFAULT_CACHE_TTL },
      });
      const data = await response.json();
      return data?.data || null;
    } catch (error) {
      console.error("Error fetching Google Tag data:", error);
      return null;
    }
  });
}
